import React, { useRef, useState } from 'react';
import { Grid, Stack, Tab, Tabs, Dialog, DialogTitle, DialogContent, Button, DialogActions, Tooltip } from '@mui/material';
import { tabProps } from '../../components/BasicTabs';
import IconButton from 'components/@extended/IconButton';
import CompanyInfoTab from './CompanyInfoTab';
import { Company, CompanyType, Rights, User, useDeleteCompanyMutation } from '../../graphql/types';
import { CloseCircleOutlined, DeleteFilled, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { openSnackbar } from 'store/reducers/snackbar';
import CompanyUserTab from './CompanyUserTab';
import useAuth from '../../hooks/useAuth';
import BankInfoTabContent, { BankInfoTabContentHandle } from 'sections/banks/BankInfoTabContent';

export interface Props {
  entity?: any;
  open?: boolean;
  onCancel: () => void;
  onGridRefresh?: () => void;
  companyTypes?: CompanyType[];
  onSave: (company: Company) => void;
  title?: string;
  associatedUsers?: User[];
  usersRefetch?: () => void;
}

const AddCompany = ({
  entity,
  open,
  onCancel,
  onSave,
  title = 'Company',
  companyTypes,
  associatedUsers = [],
  usersRefetch
}: Props) => {
  const [currentTab, setCurrentTab] = useState(0);
  const companyInfoTabRef = useRef<any>();
  const bankInfoTabRef = useRef<BankInfoTabContentHandle>(null);
  const dispatch = useDispatch();
  const { user: currentUser } = useAuth();
  const canDeleteRight =
    currentUser?.rights.includes(Rights.ManageUsers) || currentUser?.rights.includes(Rights.ManageBrokerCompanies);
  const canEdit =
    currentUser?.rights.includes(Rights.ManageMerchants) ||
    currentUser?.rights.includes(Rights.ManageMerchantCompanies) ||
    currentUser?.rights.includes(Rights.ManageBrokerCompanies);
  const canDelete = !!entity && entity.totalUsers === 0 && !entity?.mcas?.length && !entity?.bankAccounts?.length && !!canDeleteRight;
  const [deleteCompany] = useDeleteCompanyMutation();

  const handleSave = () => {
    if (!canEdit) return;
    if (companyInfoTabRef.current) {
      companyInfoTabRef.current.submitForm();
    }
  };

  const deleteHandler = async () => {
    try {
      await deleteCompany({ variables: { id: entity.id } });
      onSave(entity);
      dispatch(
        openSnackbar({
          open: true,
          message: (companyTypes?.[0] || 'Company') + ' deleted successfully.',
          variant: 'alert',
          alert: { color: 'success' },
          close: true
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Dialog open={!!open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>{entity?.id ? `Edit ${title}` : `New ${title}`}</DialogTitle>
      <DialogContent>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="Company Info" {...tabProps(0)} />
          <Tab label="Owners Info" {...tabProps(1)} />
          <Tab label="Bank Info" {...tabProps(2)} />
          {entity?.id && <Tab label="Users" {...tabProps(3)} />}
        </Tabs>

        <div style={{ display: currentTab === 0 || currentTab === 1 ? 'block' : 'none' }}>
          <CompanyInfoTab
            ref={companyInfoTabRef}
            companyTypes={companyTypes}
            entity={entity}
            onCancel={onCancel}
            onSave={onSave}
            activeSubTab={currentTab}
            onNavigateToMainTab={setCurrentTab}
            onAfterCompanyPersist={async (c) => {
              await bankInfoTabRef.current?.persistDrafts(c.id!);
            }}
          />
        </div>

        <div style={{ display: currentTab === 2 ? 'block' : 'none' }}>
          <BankInfoTabContent ref={bankInfoTabRef} variant="merchant" merchant={entity} />
        </div>

        {entity?.id && currentTab === 3 && (
          <CompanyUserTab associatedUsers={associatedUsers} company={entity} usersRefetch={usersRefetch} />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            {canDelete && (
              <Tooltip title={'Delete'} placement="top">
                <IconButton onClick={deleteHandler} size="large" color="error">
                  <DeleteFilled />
                </IconButton>
              </Tooltip>
            )}
          </Grid>
          <Grid item>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button color="error" startIcon={<CloseCircleOutlined />} onClick={onCancel} aria-label="Cancel">
                Cancel
              </Button>
              {(currentTab === 0 || currentTab === 1 || currentTab === 2) && canEdit && (
                <Button startIcon={entity ? <SaveOutlined /> : <PlusOutlined />} variant="contained" onClick={handleSave}>
                  {entity ? 'Save' : 'Add'}
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

export default AddCompany;
